/**
 * RoomMusicPlayer — background playlist for the throne hall.
 *
 * Playlist source order:
 *   1. Active launch theme's `music` array (when set in
 *      `client/public/launches/<id>/theme.json`).
 *   2. Built-in glob of `./tracks/*` — auto-discovered by Vite at build
 *      time so dropping a file in `client/src/music/tracks/` registers it
 *      with no manifest edit.
 *
 * Files play in declared order (alphabetical for the glob) and loop forever;
 * the player advances on the audio element's `ended` event.
 *
 * Volume + mute state are persisted to localStorage so they survive reloads.
 * Autoplay tries immediately; if the browser blocks it (no user gesture yet),
 * we wire a one-shot listener on pointerdown/keydown to retry — same pattern
 * the throne video screens use.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLaunchTheme } from "../launch/LaunchProvider";

const TRACK_MODULES = import.meta.glob(
  "./tracks/*.{mp3,ogg,wav,m4a,flac,webm}",
  { eager: true, query: "?url", import: "default" },
) as Record<string, string>;

const VOLUME_KEY = "solblox:music:volume";
const MUTED_KEY = "solblox:music:muted";

interface Track {
  url: string;
  name: string;
}

function buildBuiltinPlaylist(): Track[] {
  return Object.entries(TRACK_MODULES)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([path, url]) => {
      const file = path.split("/").pop() ?? "track";
      const name = file.replace(/\.[^.]+$/, "");
      return { url, name };
    });
}

function buildLaunchPlaylist(urls: ReadonlyArray<string>): Track[] {
  return urls.map((url) => {
    const tail = url.split("/").pop() ?? url;
    const name = tail.replace(/\.[^.]+$/, "");
    return { url, name };
  });
}

/**
 * Resolve a track URL to a fully-buffered in-memory Blob object URL, cached
 * per source URL.
 *
 * The built-in beds are long (the ambient track runs ~21 min / ~19MB), and
 * streaming them over range requests rebuffers mid-track whenever the dev
 * server or network is busy serving the 3D assets — heard as the music
 * "cutting in and out". Fetching the whole file once and playing it from a
 * Blob URL removes range requests entirely, so playback never underruns.
 */
function loadTrackBlobUrl(
  url: string,
  cache: Map<string, Promise<string>>,
): Promise<string> {
  const existing = cache.get(url);
  if (existing) return existing;
  const p = (async (): Promise<string> => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  })();
  // Cache the in-flight PROMISE, not just the result, so concurrent callers
  // share one fetch — a 19MB ambient bed must never download twice or mint two
  // object URLs (the second would re-seek the element to 0 and sound like a
  // cut). Drop on failure so a later attempt retries instead of caching a
  // rejection.
  cache.set(url, p);
  void p.catch(() => cache.delete(url));
  return p;
}

export function RoomMusicPlayer(): JSX.Element | null {
  const theme = useLaunchTheme();
  const playlist = useMemo(
    () =>
      theme.music && theme.music.length > 0
        ? buildLaunchPlaylist(theme.music)
        : buildBuiltinPlaylist(),
    [theme.music],
  );
  const [trackIdx, setTrackIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [volume, setVolume] = useState<number>(() => {
    const raw = Number(localStorage.getItem(VOLUME_KEY));
    return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 0.3;
  });
  const [muted, setMuted] = useState<boolean>(
    () => localStorage.getItem(MUTED_KEY) === "1",
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Cache of source URL → in-memory Blob object URL, so a track is fetched at
  // most once and looping/revisiting replays it from memory with no refetch.
  const blobUrlCacheRef = useRef<Map<string, Promise<string>>>(new Map());
  // One-shot guard so a persistent streaming fallback warns once, not per loop.
  const warnedFallbackRef = useRef(false);
  // Mirror the desired play-state into a ref so the resume watchdog (below)
  // reads the live value without re-subscribing on every toggle.
  const playingRef = useRef(playing);
  playingRef.current = playing;

  // Revoke all cached object URLs on unmount so the buffered audio is freed.
  useEffect(() => {
    const cache = blobUrlCacheRef.current;
    return () => {
      for (const p of cache.values())
        void p.then((u) => URL.revokeObjectURL(u)).catch(() => {});
      cache.clear();
    };
  }, []);

  // One <audio> element for the lifetime of the player. Advance on `ended`.
  //
  // The element is parked off-screen in the *DOM* rather than left as a bare
  // `new Audio()`: browsers gate/suspend detached media elements far more
  // aggressively (the same reason the LiveKit layer appends its <audio> to
  // document.body), which is what surfaces as the music "cutting in and out".
  // A resume watchdog + stall/pause listeners kick playback back to life after
  // network stalls, tab-throttling, or main-thread jank — without overriding a
  // deliberate user pause (guarded by playingRef).
  useEffect(() => {
    if (playlist.length === 0) return;
    const a = new Audio();
    a.preload = "auto";
    a.style.display = "none";
    document.body.appendChild(a);
    audioRef.current = a;

    const onEnded = (): void => {
      setTrackIdx((i) => (i + 1) % playlist.length);
    };

    // Recovery is FREEZE-aware, not just pause-aware. The cut-out shows up two
    // ways, and the old watchdog (`if (a.paused) play()`) only caught the first:
    //   1. element goes `paused` — browser/OS audio-focus, a rejected play();
    //      re-issue play().
    //   2. element stays `paused === false` but `currentTime` stops advancing —
    //      a buffer underrun (`waiting`/`stalled`). The old guard no-oped here,
    //      so playback only resumed when the network/decoder caught up: the
    //      multi-second gap you hear. Detect the frozen clock and nudge.
    // Runs from every stall event AND a tight 1s interval so the gap is small
    // even if an event doesn't fire.
    let lastTime = 0;
    let frozenTicks = 0;
    const ensurePlaying = (): void => {
      if (!playingRef.current || a.ended) return; // respect pause / let `ended` advance
      if (a.paused) {
        void a.play().catch(() => {});
        return;
      }
      if (a.currentTime === lastTime) {
        if (++frozenTicks >= 2) {
          // ~2s frozen while "playing" → kick it.
          frozenTicks = 0;
          void a.play().catch(() => {});
        }
      } else {
        frozenTicks = 0;
        lastTime = a.currentTime;
      }
    };

    // A media error (decode hiccup) leaves the element dead until reloaded;
    // reload the same src and resume from where we were.
    const onError = (): void => {
      if (!playingRef.current) return;
      const at = a.currentTime;
      a.load();
      const restore = (): void => {
        a.removeEventListener("loadedmetadata", restore);
        if (at > 0 && Number.isFinite(a.duration)) {
          try {
            a.currentTime = at;
          } catch {
            /* seek not available yet — start from 0 rather than stay silent */
          }
        }
        void a.play().catch(() => {});
      };
      a.addEventListener("loadedmetadata", restore);
    };

    // Tabs throttle timers/media when hidden; re-assert on return.
    const onVisible = (): void => {
      if (document.visibilityState === "visible") ensurePlaying();
    };

    a.addEventListener("ended", onEnded);
    a.addEventListener("stalled", ensurePlaying);
    a.addEventListener("waiting", ensurePlaying);
    a.addEventListener("suspend", ensurePlaying);
    a.addEventListener("pause", ensurePlaying);
    a.addEventListener("error", onError);
    document.addEventListener("visibilitychange", onVisible);
    const watchdog = window.setInterval(ensurePlaying, 1000);

    return () => {
      window.clearInterval(watchdog);
      document.removeEventListener("visibilitychange", onVisible);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("stalled", ensurePlaying);
      a.removeEventListener("waiting", ensurePlaying);
      a.removeEventListener("suspend", ensurePlaying);
      a.removeEventListener("pause", ensurePlaying);
      a.removeEventListener("error", onError);
      a.pause();
      a.removeAttribute("src");
      a.load();
      a.remove();
      audioRef.current = null;
    };
  }, [playlist.length]);

  // Swap src + play/pause whenever track index or play-state flips. The source
  // is a fully-buffered Blob URL (see loadTrackBlobUrl) so playback can't
  // underrun mid-track; we fall back to direct streaming only if the fetch
  // fails. A pause is synchronous; resolving the Blob is async, so guard
  // against the track changing while the fetch is in flight.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || playlist.length === 0) return;
    const track = playlist[trackIdx];
    if (!track) return;

    if (!playing) {
      a.pause();
      return;
    }

    let cancelled = false;
    const play = (): void => {
      a.play().catch(() => {
        const retry = (): void => {
          a.play().catch(() => {});
          window.removeEventListener("pointerdown", retry);
          window.removeEventListener("keydown", retry);
        };
        window.addEventListener("pointerdown", retry, { once: true });
        window.addEventListener("keydown", retry, { once: true });
      });
    };
    void (async () => {
      let src = track.url;
      try {
        src = await loadTrackBlobUrl(track.url, blobUrlCacheRef.current);
      } catch (err) {
        // Blob preload failed → fall back to direct streaming (range requests,
        // which CAN underrun mid-track). Warn once so a persistent cut-out is
        // traceable to the fetch rather than guessed at.
        if (!warnedFallbackRef.current) {
          warnedFallbackRef.current = true;
          // eslint-disable-next-line no-console
          console.warn(
            `[music] blob preload failed for ${track.url}; streaming directly (may stall mid-track):`,
            err,
          );
        }
      }
      if (cancelled) return;
      // Comparing against the resolved absolute form keeps a re-render (e.g. a
      // play/pause toggle) from re-seeking to 0 — only a real track change
      // swaps src.
      const desired = new URL(src, window.location.href).toString();
      if (a.src !== desired) a.src = src;
      play();
    })();
    return () => {
      cancelled = true;
    };
  }, [trackIdx, playing, playlist]);

  // Sync mute/volume to the element and persist.
  useEffect(() => {
    const a = audioRef.current;
    if (a) {
      a.volume = volume;
      a.muted = muted;
    }
    localStorage.setItem(VOLUME_KEY, String(volume));
    localStorage.setItem(MUTED_KEY, muted ? "1" : "0");
  }, [volume, muted]);

  if (playlist.length === 0) return null;

  const track = playlist[trackIdx] ?? playlist[0]!;
  const next = (): void =>
    setTrackIdx((i) => (i + 1) % playlist.length);
  const prev = (): void =>
    setTrackIdx((i) => (i - 1 + playlist.length) % playlist.length);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 16,
        right: 16,
        background: "rgba(10, 8, 14, 0.72)",
        border: "1px solid rgba(244, 211, 106, 0.35)",
        borderRadius: 8,
        padding: "8px 12px",
        color: "#fff2c8",
        font: "12px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace",
        minWidth: 240,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        userSelect: "none",
        zIndex: 50,
        pointerEvents: "auto",
      }}
    >
      <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4, letterSpacing: 1 }}>
        NOW PLAYING · {trackIdx + 1}/{playlist.length}
      </div>
      <div
        title={track.name}
        style={{
          marginBottom: 6,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {track.name}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button type="button" onClick={prev} style={btnStyle} aria-label="previous">
          {"<<"}
        </button>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          style={btnStyle}
          aria-label={playing ? "pause" : "play"}
        >
          {playing ? "||" : ">"}
        </button>
        <button type="button" onClick={next} style={btnStyle} aria-label="next">
          {">>"}
        </button>
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          style={btnStyle}
          aria-label={muted ? "unmute" : "mute"}
        >
          {muted ? "MUTED" : "SND"}
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          style={{ flex: 1, minWidth: 60, accentColor: "#f4d36a" }}
        />
      </div>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: "rgba(244, 211, 106, 0.12)",
  border: "1px solid rgba(244, 211, 106, 0.35)",
  color: "#fff2c8",
  borderRadius: 4,
  padding: "2px 8px",
  cursor: "pointer",
  fontSize: 12,
  fontFamily: "inherit",
  minWidth: 28,
};
